/* ============================================================================
 * AE Foodstore — product-card inline quantity stepper (Đợt 2, Layer 2 core)
 * Spec: .cowork-context/project_weee_addcart_feature_spec.md
 * Anti-regression rules: .cowork-context/feedback_cart_ui_anti_regression.md
 *
 * SINGLE SOURCE OF TRUTH — what THIS file is allowed to write:
 *   - The per-card stepper UI ONLY: the .card__stepper element (qty number,
 *     +/−/trash buttons, its labels) and the .is-in-cart class on the card
 *     (which hides the "+"). Nothing else in the theme writes these.
 *   - It NEVER writes #Cartcount, #CartTotalText, or the mini-cart popup. Those
 *     have ONE writer — syncCountAndState() in mini-cart-popup.js — driven by the
 *     `cart:updated` event. After a qty change we dispatch cart:updated{count,total}
 *     with values taken STRAIGHT FROM the /cart/change.js response (full cart
 *     state), so there is no delta math to get wrong (BUG-B).
 *
 * SERIALIZATION — qty changes share the SAME window-level queue the "+" add uses
 *   (window.__aefCartAddQueue) and bump window.__aefCartAddPending, so adds and
 *   qty changes never run in parallel and cart-card-indicator.js skips reconciling
 *   mid-flight (BUG-O / BUG-Z). One /cart/change.js per click-burst (debounced).
 *
 * FIRST ADD still flows through the existing "+" path (mini-cart queueAdd). We
 *   only listen for the resulting cart:updated and reveal the stepper. Stepper
 *   buttons are type="button" (NOT submit) so neither perf-instant.js nor the
 *   mini-cart add-queue (both match .card__quick-add / [type=submit]) touch them.
 *
 * Endpoint shapes (Shopify): /cart/change.js {id, quantity} sets ABSOLUTE qty and
 *   returns full CART STATE; /cart.js returns full CART STATE. `id` accepts the
 *   numeric variant id, which is what the card carries (data-variant-id).
 *
 * Layer 2 scope: core mechanics with min=1 / no max. MIN jump + min>1 stuck land
 *   in L3; MAX cap + tooltip in L4; mobile count-circle in L5.
 * ========================================================================== */
(function () {
  'use strict';
  if (window.__aefCardStepperBound) return;
  window.__aefCardStepperBound = true;

  var COMMIT_DEBOUNCE_MS = 280; // collapse a click-burst into one /cart/change.js
  var SETTLE_MS = 50;           // let Shopify's cart-cookie lock release between ops

  // variant id -> optimistic target qty while a burst/change is in flight.
  // While set for a variant, server reconciles skip it so the optimistic number
  // isn't clobbered by a stale /cart.js mid-burst.
  var optimistic = {};
  var burstTimer = {}; // variant id -> debounce timer

  var TRASH_SVG =
    '<svg viewBox="0 0 24 24" width="15" height="15" fill="none" stroke="currentColor"' +
    ' stroke-width="2" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true">' +
    '<polyline points="3 6 5 6 21 6"></polyline>' +
    '<path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6m3 0V4a2 2 0 0 1 2-2h4a2 2 0 0 1 2 2v2"></path>' +
    '</svg>';
  var MINUS_HTML = '&minus;';

  function cardsFor(vid) {
    // querySelectorAll (not getElementById) — the same variant can appear on
    // multiple cards across sections (anti-regression Pattern 3).
    return document.querySelectorAll('.wbproduct-container[data-variant-id="' + vid + '"]');
  }

  // Per-product quantity rules from the card's metafield-driven data attributes
  // (set in product-card.liquid). min defaults to 1, max 0 means "no cap".
  function rulesFor(card) {
    var min = parseInt(card.getAttribute('data-min-qty'), 10);
    var max = parseInt(card.getAttribute('data-max-qty'), 10);
    if (!(min >= 1)) min = 1;
    if (!(max >= 1)) max = 0;
    return { min: min, max: max };
  }

  function paintCard(card, qty) {
    var stepper = card.querySelector('[data-card-stepper]');
    if (!stepper) return;
    var qtyEl = stepper.querySelector('[data-step-qty]');
    var downBtn = stepper.querySelector('[data-step-down]');
    var downIcon = stepper.querySelector('[data-step-down-icon]');
    var upBtn = stepper.querySelector('[data-step-up]');
    var rules = rulesFor(card);

    if (qty > 0) {
      card.classList.add('is-in-cart');
      stepper.hidden = false;
      stepper.setAttribute('aria-hidden', 'false');
      if (qtyEl) qtyEl.textContent = String(qty);
      // Left button: trash (remove) only when the minimum allows going to 0,
      // i.e. min<=1 and qty at the floor. When min>1, the line can't be removed
      // from the card (Boss decision) — at the floor the "−" is shown disabled
      // (stuck); above the floor it decrements.
      var atMin = qty <= rules.min;
      var isRemove = atMin && rules.min <= 1;
      var stuck = rules.min > 1 && atMin;
      if (downIcon) downIcon.innerHTML = isRemove ? TRASH_SVG : MINUS_HTML;
      if (downBtn) {
        downBtn.classList.toggle('card__step--trash', isRemove);
        downBtn.disabled = stuck;
        downBtn.setAttribute('aria-disabled', stuck ? 'true' : 'false');
        downBtn.setAttribute('aria-label', isRemove ? 'Remove from cart' : 'Decrease quantity');
      }
      // At the cap, fade the + as an affordance (it still fires → shows the tooltip).
      if (upBtn) upBtn.classList.toggle('card__step--maxed', rules.max > 0 && qty >= rules.max);
    } else {
      card.classList.remove('is-in-cart');
      stepper.hidden = true;
      stepper.setAttribute('aria-hidden', 'true');
      if (qtyEl) qtyEl.textContent = '0';
    }
  }

  function paintVariant(vid, qty) {
    var cards = cardsFor(vid);
    for (var i = 0; i < cards.length; i++) paintCard(cards[i], qty);
  }

  function qtyOfVariant(cart, vid) {
    var q = 0;
    var items = (cart && cart.items) || [];
    for (var i = 0; i < items.length; i++) {
      if (String(items[i].variant_id) === String(vid)) q += items[i].quantity;
    }
    return q;
  }

  function dispatchCartUpdated(count, total) {
    // Hand the new totals to the single source (mini-cart syncCountAndState).
    var detail = { source: 'card-stepper' };
    if (typeof count === 'number') detail.count = count;
    if (typeof total === 'number') detail.total = total;
    try {
      document.dispatchEvent(new CustomEvent('cart:updated', { detail: detail }));
    } catch (e) {
      var ev = document.createEvent('Event');
      ev.initEvent('cart:updated', true, true);
      document.dispatchEvent(ev);
    }
  }

  function settle(ms) {
    return new Promise(function (resolve) { setTimeout(resolve, ms); });
  }

  // MAX-limit notice — black tooltip "Đã đạt giới hạn mua" above the + for ~1.6s.
  var tooltipTimers = {};
  function showTooltip(stepper) {
    var tip = stepper.querySelector('[data-step-tooltip]');
    if (!tip) return;
    var key = stepper.getAttribute('data-variant-id') || '';
    tip.hidden = false;
    void tip.offsetWidth; // reflow so the opacity transition runs
    tip.classList.add('is-visible');
    if (tooltipTimers[key]) clearTimeout(tooltipTimers[key]);
    tooltipTimers[key] = setTimeout(function () {
      tip.classList.remove('is-visible');
      setTimeout(function () { tip.hidden = true; }, 220);
    }, 1600);
  }

  // Commit the final target qty for one variant via /cart/change.js, serialized
  // on the shared add/change queue.
  function commitChange(vid, qty) {
    window.__aefCartAddQueue = window.__aefCartAddQueue || Promise.resolve();
    window.__aefCartAddPending = (window.__aefCartAddPending || 0) + 1;

    window.__aefCartAddQueue = window.__aefCartAddQueue.then(function () {
      return fetch('/cart/change.js', {
        method: 'POST',
        credentials: 'same-origin',
        headers: { 'Content-Type': 'application/json', 'X-Requested-With': 'XMLHttpRequest' },
        body: JSON.stringify({ id: vid, quantity: qty }),
      })
        .then(function (r) { return r.json().catch(function () { return null; }); })
        .then(function (cart) {
          if (cart && typeof cart.item_count === 'number') {
            // Paint from the authoritative response, then hand totals to the
            // single source. NEVER write the badge/total here directly.
            // STEPPER-RACE-1: only paint when this commit is still the latest
            // intent for the variant. If a newer burst raised optimistic[vid]
            // after we were queued, painting the (now stale) server qty would
            // snap the visible number back down; the newer burst's own commit
            // (already scheduled via burstTimer) paints the right value.
            if (optimistic[vid] == null || optimistic[vid] === qty) {
              paintVariant(vid, qtyOfVariant(cart, vid));
            }
            // BUG-H: /cart/change.js returns an INFLATED total_price for
            // ~200-400ms (a phantom +$10.99 constant from some server-side
            // source — verified no discount/Function in play; total_discount:0).
            // items_subtotal_price (= sum of line_prices) and the follow-up
            // /cart.js GET are correct. Hand the reliable subtotal to the single
            // writer so the header total never flashes the ghost value. Fallbacks
            // keep it working if a future Shopify response omits the field.
            var reliableTotal =
              typeof cart.items_subtotal_price === 'number' ? cart.items_subtotal_price :
              typeof cart.original_total_price === 'number' ? cart.original_total_price :
              cart.total_price;
            dispatchCartUpdated(cart.item_count, reliableTotal);
          }
          return settle(SETTLE_MS);
        })
        .catch(function (err) {
          if (window.console && console.warn) console.warn('[card-stepper] change failed', err);
        })
        .then(function () {
          // STEPPER-RACE-1: clear the in-flight marker only if we're still the
          // latest target. A newer burst's value must survive so its queued
          // commit reads the right qty — otherwise the next commitChange fires
          // with optimistic[vid] === undefined → /cart/change.js quantity:undefined
          // → the increment is silently lost.
          if (optimistic[vid] === qty) delete optimistic[vid];
          window.__aefCartAddPending = Math.max(0, (window.__aefCartAddPending || 1) - 1);
        });
    });
  }

  // ---- stepper +/− clicks (delegated; buttons are type=button, no submit) ----
  document.addEventListener('click', function (e) {
    if (!e.target || !e.target.closest) return;
    var up = e.target.closest('[data-step-up]');
    var down = up ? null : e.target.closest('[data-step-down]');
    if (!up && !down) return;
    var btn = up || down;
    var stepper = btn.closest('[data-card-stepper]');
    var card = btn.closest('.wbproduct-container');
    if (!stepper || !card) return;
    e.preventDefault();

    var vid = card.getAttribute('data-variant-id');
    if (!vid) return;

    var qtyEl = stepper.querySelector('[data-step-qty]');
    var cur = optimistic[vid] != null
      ? optimistic[vid]
      : (parseInt(qtyEl && qtyEl.textContent, 10) || 0);
    var rules = rulesFor(card);

    var next;
    if (up) {
      next = cur + 1;
      if (rules.max > 0 && next > rules.max) {
        showTooltip(stepper); // at the cap — refuse the increment + notify
        return;
      }
    } else if (cur <= rules.min) {
      if (rules.min > 1) return; // min>1 → stuck at the minimum, no remove (Boss)
      next = 0;                  // min<=1 → trash removes the line
    } else {
      next = cur - 1;
    }
    if (next === cur) return;

    optimistic[vid] = next;
    paintVariant(vid, next); // optimistic, instant — monotonic display under rapid clicks

    if (burstTimer[vid]) clearTimeout(burstTimer[vid]);
    burstTimer[vid] = setTimeout(function () {
      delete burstTimer[vid];
      // STEPPER-RACE-1: if an earlier resolve already committed+cleared this
      // variant (e.g. a net-zero burst that landed back on the committed qty),
      // there's nothing new to send — never call change.js with quantity:undefined.
      if (optimistic[vid] == null) return;
      commitChange(vid, optimistic[vid]);
    }, COMMIT_DEBOUNCE_MS);
  }, false);

  // ---- mobile count-circle (<=480px): collapsed stepper is a circle showing the
  // qty; first tap expands to the full [🗑/− N +], a tap elsewhere (or 3s idle)
  // collapses it back. Pure class toggle — the <=480px CSS does the visual; on
  // desktop .is-expanded is inert so this is a no-op there. Runs in CAPTURE so a
  // tap on the collapsed circle can stop the +/− handler from also firing. ----
  var collapseTimer = {};
  function isMobile() { return window.matchMedia('(max-width: 480px)').matches; }
  function scheduleCollapse(stepper) {
    var key = stepper.getAttribute('data-variant-id') || '';
    if (collapseTimer[key]) clearTimeout(collapseTimer[key]);
    collapseTimer[key] = setTimeout(function () { stepper.classList.remove('is-expanded'); }, 3000);
  }
  document.addEventListener('click', function (e) {
    if (!isMobile() || !e.target || !e.target.closest) return;
    var stepper = e.target.closest('[data-card-stepper]');
    if (!stepper) {
      // tap outside any stepper → collapse all expanded circles
      var open = document.querySelectorAll('[data-card-stepper].is-expanded');
      for (var i = 0; i < open.length; i++) open[i].classList.remove('is-expanded');
      return;
    }
    if (!stepper.classList.contains('is-expanded')) {
      // collapsed circle tapped → expand only (do NOT change qty); stop the
      // +/− bubble handler from seeing this tap.
      e.preventDefault();
      e.stopPropagation();
      var others = document.querySelectorAll('[data-card-stepper].is-expanded');
      for (var j = 0; j < others.length; j++) {
        if (others[j] !== stepper) others[j].classList.remove('is-expanded');
      }
      stepper.classList.add('is-expanded');
    }
    // expanded already → let the +/− handler run; either way keep it open a bit
    scheduleCollapse(stepper);
  }, true);

  // ---- reconcile all steppers from server cart state ----
  function reconcileAll(cart) {
    var cards = document.querySelectorAll('.wbproduct-container[data-variant-id]');
    for (var i = 0; i < cards.length; i++) {
      var card = cards[i];
      var vid = card.getAttribute('data-variant-id');
      if (optimistic[vid] != null) continue; // a burst/change is in flight — leave it
      paintCard(card, qtyOfVariant(cart, vid));
    }
  }

  var reconcileTimer = null;
  function scheduleReconcile() {
    if (reconcileTimer) clearTimeout(reconcileTimer);
    reconcileTimer = setTimeout(function () {
      reconcileTimer = null;
      fetch('/cart.js', { credentials: 'same-origin', cache: 'no-store' })
        .then(function (r) { return r.json(); })
        .then(reconcileAll)
        .catch(function () {});
    }, 120);
  }

  // Reveal stepper after the first "+" add + keep all cards in sync with any cart
  // mutation. READ + paint of our own stepper DOM only — never writes shared cart UI.
  //
  // BUG-F fast path: the mini-cart add-queue's cart:updated carries detail.line
  // (the /cart/add.js response: variant_id + cumulative quantity — see BUG-7/BUG-B).
  // Paint that variant's stepper IMMEDIATELY so the card transforms in the SAME tick
  // as the header badge, instead of waiting ~1s for the debounced /cart.js reconcile
  // (which would leave "+Add" showing while the cart already has the item → users
  // re-tap → an extra add). The full reconcile still runs as a backstop.
  // Skipped when an optimistic stepper burst is in flight for that variant (its own
  // resolve paints it) and for our own cart:updated (source 'card-stepper', no line).
  document.addEventListener('cart:updated', function (e) {
    var line = e && e.detail && e.detail.line;
    if (line) {
      var vid = line.variant_id != null ? String(line.variant_id)
              : (line.id != null ? String(line.id) : null);
      if (vid && optimistic[vid] == null && typeof line.quantity === 'number') {
        paintVariant(vid, line.quantity);
      }
    }
    scheduleReconcile();
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', scheduleReconcile);
  } else {
    scheduleReconcile();
  }
})();
